import React, { useState, useCallback } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  ImageInput,
  ImageField,
  FileInput,
  FileField,
  BooleanInput,
  SelectInput,
  SaveButton,
  Toolbar,
  required,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { withStyles } from '@mui/styles';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import LaunchIcon from '@mui/icons-material/Launch';
import dataProvider from '../../../providers/dataProvider';
import uploadPicture from '../../../utils/uploadPicture';
import ImagePickerButton from '../../../components/ImagePickerButton/ImagePickerButton';
import uploadVideoService from '../../../utils/uploadVideo';
import Loading from '../../../components/Loading/Loading';
import countUploading from '../../../utils/countUploading';
import exerciseType from '../../../constants/exerciseType';
import VideoPickerButton from '../../../components/VideoPickerButton';

const CREATE = 'CREATE';

const saveExercise = (values) => {
  return dataProvider(CREATE, 'exercise', { data: values });
};

// Component to display selected video from library and handle clearing
const SelectedVideoDisplay = ({ video, onClear }) => {
  if (!video) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        mb: 1,
        bgcolor: 'success.light',
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" sx={{ flex: 1 }}>
        Selected: <strong>{video.originalName || video.fileName || `Video #${video.id}`}</strong>
        {video.muxPlaybackId && ` (${video.muxPlaybackId})`}
      </Typography>
      {video.muxPlaybackId && (
        <IconButton
          href={`https://stream.mux.com/${video.muxPlaybackId}/low.mp4`}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          title="Preview video"
        >
          <LaunchIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton size="small" onClick={onClear} title="Clear selection">
        <ClearIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// Wrapper component that provides form context to VideoPickerButton
const VideoPickerWithForm = ({ selectedVideo, onVideoSelect, onClear }) => {
  const formContext = useFormContext();

  const handleSelect = useCallback(
    (video) => {
      onVideoSelect(video);
      // Clear the file input when a video is selected from library
      if (formContext?.setValue) {
        formContext.setValue('file', null);
      }
    },
    [formContext, onVideoSelect],
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Or select an existing video from the library:
      </Typography>
      <SelectedVideoDisplay video={selectedVideo} onClear={onClear} />
      <VideoPickerButton onSelect={handleSelect} />
    </Box>
  );
};

const ExercisesCreateToolbar = ({ loading, progressImg, progressVideo, ...props }) => {
  return (
    <>
      <Loading open={loading}>
        <>
          <p>Uploading Exercise Photo {progressImg} %</p>
          <LinearProgress value={progressImg} variant="determinate" />
          <p>Uploading Demo video {progressVideo} %</p>
          <LinearProgress value={progressVideo} variant="determinate" />
        </>
      </Loading>
      <Toolbar {...props}>
        <SaveButton />
      </Toolbar>
    </>
  );
};

const ExercisesCreate = ({ classes, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();

  const [state, setState] = useState({
    loading: false,
    progressImg: 0,
    progressVideo: 0,
  });

  const [selectedLibraryVideo, setSelectedLibraryVideo] = useState(null);

  const progressCountImg = useCallback((progressEvent) => {
    setState((prev) => ({ ...prev, progressImg: countUploading(progressEvent) }));
  }, []);

  const progressCountVideo = useCallback((progressEvent) => {
    setState((prev) => ({ ...prev, progressVideo: countUploading(progressEvent) }));
  }, []);

  const uploadImg = useCallback((img) => uploadPicture(img, progressCountImg), [progressCountImg]);

  const uploadVideo = useCallback(
    (video) => {
      return uploadVideoService(video, progressCountVideo);
    },
    [progressCountVideo],
  );

  const handleSubmit = useCallback(
    async (values) => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        // Check if we have a video from library or need to upload
        const hasLibraryVideo = selectedLibraryVideo?.id;
        const hasFileToUpload = values.file?.rawFile;

        if (!hasLibraryVideo && !hasFileToUpload) {
          throw new Error('Please select or upload a video');
        }

        const [imgResult, videoResult] = await Promise.all([
          uploadImg(values.picture),
          hasLibraryVideo
            ? Promise.resolve({ id: selectedLibraryVideo.id })
            : uploadVideo(values.file),
        ]);
        if (!imgResult?.data?.fileName) {
          throw new Error('Failed to upload image');
        }
        if (!videoResult?.id) {
          throw new Error('Failed to upload video');
        }
        await saveExercise({
          ...values,
          type: values.type,
          picture: imgResult.data.fileName,
          videoId: videoResult.id,
        });
        notify('Exercise created successfully', { type: 'success' });
        redirect('/exercise');
      } catch (error) {
        notify(error?.message || 'Failed to create exercise', { type: 'error' });
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [uploadImg, uploadVideo, notify, redirect, selectedLibraryVideo],
  );

  const { progressImg, progressVideo, loading } = state;

  return (
    <>
      <Create {...props}>
        <SimpleForm
          onSubmit={handleSubmit}
          toolbar={
            <ExercisesCreateToolbar
              loading={loading}
              progressImg={progressImg}
              progressVideo={progressVideo}
            />
          }
        >
          <TextInput label="Exercise Name" source="name" validate={required()} />
          <SelectInput
            label="Exercise Type"
            source="type"
            choices={[
              { id: 1, name: exerciseType[1] },
              { id: 2, name: exerciseType[2] },
              { id: 3, name: exerciseType[3] },
            ]}
            defaultValue={2}
          />
          <ImageInput
            formClassName={classes.inlineBlock}
            source="picture"
            label="Exercise Photo"
            validate={required()}
            accept={{ 'image/*': [] }}
          >
            <ImageField source="src" title="title" />
          </ImageInput>
          <ImagePickerButton source="picture" label={false} />
          <FileInput
            source="file"
            label="Demo/Lesson video (upload new)"
            accept={{ 'video/mp4': ['.mp4'] }}
            validate={selectedLibraryVideo ? undefined : required()}
            formClassName={classes.inlineBlock}
          >
            <FileField source="src" title="title" />
          </FileInput>
          <VideoPickerWithForm
            selectedVideo={selectedLibraryVideo}
            onVideoSelect={setSelectedLibraryVideo}
            onClear={() => setSelectedLibraryVideo(null)}
          />
          <TextInput source="description" label="Description" validate={required()} />
          <BooleanInput
            label="Ask user to switch sides and repeat exercise"
            source="forEachSide"
            defaultValue={false}
          />
          <BooleanInput
            label="Hide done button in timer"
            source="doneButton"
            defaultValue={false}
          />
        </SimpleForm>
      </Create>
    </>
  );
};

const styles = {
  inlineBlock: { display: 'inline-flex', marginRight: '1rem' },
};

export default withStyles(styles)(ExercisesCreate);
